import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';

/** How many rows a table renders at first and adds each time. */
export const RENDER_STEP = 50;

/**
 * The rows a table renders of a list it has completely: the first `step`
 * ones, `step` more on every `more()`. A new filter or sorting starts again
 * with `reset()`.
 */
export class IncrementalRows {
  public limit: number;

  constructor(public readonly step = RENDER_STEP) {
    this.limit = step;
  }

  public slice<T>(rows: readonly T[]): T[] {
    return rows.slice(0, this.limit);
  }

  public hasMore(total: number) {
    return total > this.limit;
  }

  /** Renders `step` more rows; false when all were rendered already. */
  public more(total: number) {
    if (!this.hasMore(total)) {
      return false;
    }
    this.limit += this.step;
    return true;
  }

  public reset() {
    this.limit = this.step;
  }
}

/**
 * The nearest ancestor that scrolls its content: a table wrapper that fills
 * the window, a list with a maximum height, the scrolling main area. One
 * that may scroll but grows with its content (a table wrapper on a phone)
 * does not count, the sentinel would always be in its reach.
 */
const scrollParent = (element: HTMLElement): HTMLElement | null => {
  for (
    let parent = element.parentElement;
    parent && parent !== document.body;
    parent = parent.parentElement
  ) {
    const { overflowY } = getComputedStyle(parent);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }
  }
  return null;
};

/**
 * A sentinel at the end of a table: emits `appLoadMore` when it comes within
 * `loadMoreMargin` pixels of the visible part of its scroll container (the
 * nearest scrolling ancestor, else the window). While it stays that close
 * after the new rows were rendered, for a window taller than the rows, it
 * emits again on the next frame, so the rows fill the window. The table
 * removes the sentinel once all rows are rendered.
 */
@Directive({
  selector: '[appLoadMore]',
  standalone: true,
})
export class LoadMoreDirective implements AfterViewInit, OnDestroy {
  private element = inject<ElementRef<HTMLElement>>(ElementRef);
  private zone = inject(NgZone);

  @Input() loadMoreMargin = 200;
  @Output() appLoadMore = new EventEmitter<void>();

  private observer: IntersectionObserver | null = null;
  private frame = 0;

  ngAfterViewInit() {
    this.observe();
  }

  ngOnDestroy() {
    cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
    this.observer = null;
  }

  /**
   * Watches the sentinel within its scroll container, which can change once
   * more rows are rendered: the rows may overflow a wrapper only now.
   * Observing again reports the sentinel once more if it is still in reach;
   * the observer itself reports changes only.
   */
  private observe() {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    const element = this.element.nativeElement;
    const root = scrollParent(element);
    this.zone.runOutsideAngular(() => {
      if (this.observer && this.observer.root === root) {
        this.observer.unobserve(element);
      } else {
        this.observer?.disconnect();
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.zone.run(() => this.appLoadMore.emit());
              this.recheck();
            }
          },
          { root, rootMargin: `${this.loadMoreMargin}px` }
        );
      }
      this.observer.observe(element);
    });
  }

  private recheck() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      if (this.observer) {
        this.observe();
      }
    });
  }
}
